import type { InputHTMLAttributes, Ref } from "react";
import { cssVars } from "../css-vars";
import { cn } from "../lib/cn";
import { Input } from "./field";

function percentOf(value: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(Math.max((value / max) * 100, 0), 100);
}

export type ProgressTone = "primary" | "success" | "warning" | "danger";

const FILL: Record<ProgressTone, string> = {
  primary: "bg-primary",
  success: "bg-success",
  warning: "bg-accent",
  danger: "bg-danger-fill",
};

export interface ProgressProps {
  value: number;
  max?: number;
  /** Accessible name, e.g. "Uploading Student Handbook.pdf". */
  label: string;
  /** Spoken value, e.g. "62 percent, Chunking". */
  valueText?: string;
  tone?: ProgressTone;
  size?: "sm" | "md";
  className?: string;
}

/** Task progress bar (role=progressbar); width via `--geo-width`. */
export function Progress({ value, max = 100, label, valueText, tone = "primary", size = "md", className }: ProgressProps) {
  const pct = percentOf(value, max);
  const height = size === "sm" ? "h-1" : "h-1.5";
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-valuetext={valueText}
      className={cn("rounded-pill bg-sunken", height, className)}
    >
      <div
        className={cn("w-[var(--geo-width)] rounded-pill transition-[width] duration-500 motion-reduce:transition-none", height, FILL[tone])}
        style={cssVars({ "--geo-width": `${pct}%` })}
      />
    </div>
  );
}

export interface MeterProps {
  value: number;
  max?: number;
  label: string;
  /** Visible readout, e.g. "6,240 / 10,000". */
  valueLabel?: string;
  /** Threshold marker (e.g. eval pass line) as a value on the same scale. */
  threshold?: number;
  thresholdLabel?: string;
  /** Fraction (0–1) above which the fill turns warning. */
  warnAt?: number;
  className?: string;
}

/** Usage / metric bar (role=meter) with optional threshold marker; colour change is paired with the readout text. */
export function Meter({ value, max = 100, label, valueLabel, threshold, thresholdLabel, warnAt, className }: MeterProps) {
  const pct = percentOf(value, max);
  const warn = warnAt !== undefined && pct / 100 >= warnAt;
  return (
    <div className={className}>
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold text-fg-muted">{label}</span>
        {valueLabel ? <span className="font-semibold text-fg tabular-nums">{valueLabel}</span> : null}
      </div>
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={valueLabel}
        className="relative mt-1.5 h-2 rounded-pill bg-sunken"
      >
        <div className={cn("h-2 w-[var(--geo-width)] rounded-pill", warn ? "bg-accent" : "bg-primary")} style={cssVars({ "--geo-width": `${pct}%` })} />
        {threshold !== undefined ? (
          <span
            title={thresholdLabel}
            className="absolute -top-1 left-[var(--geo-x)] h-4 w-0.5 -translate-x-1/2 rounded-pill bg-fg"
            style={cssVars({ "--geo-x": `${percentOf(threshold, max)}%` })}
          />
        ) : null}
      </div>
      {threshold !== undefined && thresholdLabel ? <p className="mt-1 text-[11px] text-fg-subtle">{thresholdLabel}</p> : null}
    </div>
  );
}

export interface DatePickerProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange"> {
  /** ISO date `YYYY-MM-DD` or "" when empty. */
  value: string;
  onValueChange: (value: string) => void;
  ref?: Ref<HTMLInputElement>;
}

/** DatePicker-lite: native date input styled as `.input` (accessible picker + keyboard entry for free). */
export function DatePicker({ value, onValueChange, className, ...rest }: DatePickerProps) {
  return (
    <Input
      type="date"
      value={value}
      onChange={(event) => {
        onValueChange(event.target.value);
      }}
      className={cn("tabular-nums", className)}
      {...rest}
    />
  );
}
